import { useEffect, useState } from 'react';

import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { Area, Bar, CartesianGrid, Cell, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { LedgerUsageTimePoint } from '@/api/queries/useGetLedgerBillingOverview';
import { Button } from '@/new/components/shadcn/button';
import { Calendar } from '@/new/components/shadcn/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';

type UsageGranularity = 'day' | 'week' | 'month';

interface ChartPoint extends LedgerUsageTimePoint {
  label: string;
  cumulativeCredits: number;
  cumulativeKimCredits: number;
  cumulativePagesCredits: number;
}

function BarHoverCursor({
  points,
  width = 0,
  height = 0,
  left = 0,
  top = 0,
  dataLength,
}: {
  points?: Array<{ x: number; y: number }>;
  width?: number;
  height?: number;
  left?: number;
  top?: number;
  dataLength: number;
}) {
  if (!points?.[0] || dataLength === 0) return null;

  const bandWidth = width / dataLength;
  const x = Math.max(left, Math.min(points[0].x - bandWidth / 2, left + width - bandWidth));
  return <rect x={x} y={top} width={bandWidth} height={height} rx={4} fill="var(--color-gray-50)" />;
}

const numberFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
const compactFormatter = new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 });
const dayFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const monthFormatter = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

function parseDate(value: string): Date {
  return new Date(`${value.slice(0, 10)}T00:00:00Z`);
}

function toDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function startOfPeriod(date: Date, granularity: UsageGranularity): Date {
  const result = new Date(date);
  if (granularity === 'week') {
    const daysSinceMonday = (result.getUTCDay() + 6) % 7;
    result.setUTCDate(result.getUTCDate() - daysSinceMonday);
  } else if (granularity === 'month') {
    result.setUTCDate(1);
  }
  return result;
}

function nextPeriod(date: Date, granularity: UsageGranularity): Date {
  const result = new Date(date);
  if (granularity === 'day') result.setUTCDate(result.getUTCDate() + 1);
  if (granularity === 'week') result.setUTCDate(result.getUTCDate() + 7);
  if (granularity === 'month') result.setUTCMonth(result.getUTCMonth() + 1);
  return result;
}

function formatPeriodLabel(date: Date, granularity: UsageGranularity): string {
  if (granularity === 'week') return dayFormatter.format(date);
  if (granularity === 'month') return monthFormatter.format(date);
  return dayFormatter.format(date);
}

function buildChartData(
  timeSeries: LedgerUsageTimePoint[],
  granularity: UsageGranularity,
  dateRange: DateRange,
): ChartPoint[] {
  if (timeSeries.length === 0 || !dateRange.from) return [];

  const selectedStartKey = format(dateRange.from, 'yyyy-MM-dd');
  const selectedEndKey = format(dateRange.to ?? dateRange.from, 'yyyy-MM-dd');
  const filteredTimeSeries = timeSeries.filter(
    (item) => item.date.slice(0, 10) >= selectedStartKey && item.date.slice(0, 10) <= selectedEndKey,
  );

  const grouped = new Map<string, LedgerUsageTimePoint>();
  for (const item of filteredTimeSeries) {
    const date = startOfPeriod(parseDate(item.date), granularity);
    const key = toDateKey(date);
    const current = grouped.get(key) ?? { date: key, pagesCredits: 0, kimCredits: 0 };
    current.pagesCredits += item.pagesCredits;
    current.kimCredits += item.kimCredits;
    grouped.set(key, current);
  }

  const rangeStart = startOfPeriod(parseDate(selectedStartKey), granularity);
  const rangeEnd = startOfPeriod(parseDate(selectedEndKey), granularity);

  const data: ChartPoint[] = [];
  let cumulativePagesCredits = 0;
  let cumulativeKimCredits = 0;
  for (let period = rangeStart; period <= rangeEnd; period = nextPeriod(period, granularity)) {
    const key = toDateKey(period);
    const item = grouped.get(key) ?? { date: key, pagesCredits: 0, kimCredits: 0 };
    cumulativePagesCredits += item.pagesCredits;
    cumulativeKimCredits += item.kimCredits;
    data.push({
      ...item,
      label: formatPeriodLabel(period, granularity),
      cumulativeCredits: cumulativePagesCredits + cumulativeKimCredits,
      cumulativeKimCredits,
      cumulativePagesCredits,
    });
  }
  return data;
}

export function UsageHistoryChart({
  timeSeries,
  dateRange,
  onDateRangeChange,
}: {
  timeSeries: LedgerUsageTimePoint[];
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
}) {
  const [granularity, setGranularity] = useState<UsageGranularity>('week');
  const [showCumulative, setShowCumulative] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [draftDateRange, setDraftDateRange] = useState<DateRange>(dateRange);
  const data = buildChartData(timeSeries, granularity, dateRange);
  const today = new Date();

  useEffect(() => setDraftDateRange(dateRange), [dateRange]);

  return (
    <section className="overflow-hidden rounded-xl border bg-white">
      <div className="border-b px-6 py-5">
        <h2 className="font-medium text-gray-900">Usage history</h2>
        <p className="mt-0.5 text-sm text-gray-500">
          Credits used over time, split by pages processed and Kim threads.
        </p>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_22rem]">
        {data.length === 0 ? (
          <div className="flex h-80 items-center justify-center px-6 text-sm text-gray-500">
            No usage has been recorded for this period.
          </div>
        ) : (
          <div className="min-w-0 px-4 pt-6 pb-4 sm:px-6">
            <div className="mb-4 flex h-4 items-center justify-end gap-5 text-xs text-gray-600">
              {showCumulative ? (
                <span className="flex items-center gap-2">
                  <span className="h-0.5 w-3.5 rounded-full bg-teal-500" /> Cumulative credits
                </span>
              ) : (
                <>
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-sm bg-gray-700" /> Pages processed
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-sm bg-teal-500" /> Kim threads
                  </span>
                </>
              )}
            </div>
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart
                data={data}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                barCategoryGap="30%"
                barSize={16}
                onMouseMove={(state) => setHoveredIndex(state.activeTooltipIndex ?? null)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <defs>
                  <linearGradient id="cumulativeCreditsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-teal-500)" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="var(--color-teal-500)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-gray-200)" />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={32}
                  tickMargin={12}
                  tick={{ fill: 'var(--color-gray-500)', fontSize: 12 }}
                  height={48}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'var(--color-gray-500)', fontSize: 12 }}
                  tickFormatter={(value: number) => compactFormatter.format(value)}
                  width={48}
                />
                <Tooltip
                  cursor={
                    showCumulative ? { stroke: 'var(--color-gray-200)' } : <BarHoverCursor dataLength={data.length} />
                  }
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const item = payload[0].payload as ChartPoint;
                    const pagesCredits = showCumulative ? item.cumulativePagesCredits : item.pagesCredits;
                    const kimCredits = showCumulative ? item.cumulativeKimCredits : item.kimCredits;
                    return (
                      <div className="min-w-56 rounded-lg border bg-white p-3 text-sm shadow-lg/4">
                        <p className="mb-2 border-b pb-2 font-medium text-gray-800">
                          {granularity === 'week' ? `w/c ${item.label}` : item.label}
                        </p>
                        <div className="space-y-1.5">
                          <div className="flex justify-between gap-6 text-gray-600">
                            <span>Pages processed</span>
                            <span className="font-medium text-gray-900">{numberFormatter.format(pagesCredits)}</span>
                          </div>
                          <div className="flex justify-between gap-6 text-gray-600">
                            <span>Kim threads</span>
                            <span className="font-medium text-gray-900">{numberFormatter.format(kimCredits)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
                {showCumulative ? (
                  <Area
                    type="monotone"
                    dataKey="cumulativeCredits"
                    name="Cumulative credits"
                    stroke="var(--color-teal-500)"
                    strokeWidth={2}
                    fill="url(#cumulativeCreditsFill)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ) : (
                  <>
                    <Bar
                      dataKey="pagesCredits"
                      name="Pages processed"
                      stackId="credits"
                      fill="var(--color-gray-700)"
                      radius={[4, 4, 4, 4]}
                      stroke="white"
                      strokeWidth={2}
                    >
                      {data.map((item, index) => (
                        <Cell key={item.date} fillOpacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.4} />
                      ))}
                    </Bar>
                    <Bar
                      dataKey="kimCredits"
                      name="Kim threads"
                      stackId="credits"
                      fill="var(--color-teal-500)"
                      radius={[4, 4, 4, 4]}
                      stroke="white"
                      strokeWidth={2}
                    >
                      {data.map((item, index) => (
                        <Cell key={item.date} fillOpacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.4} />
                      ))}
                    </Bar>
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        <aside className="border-l">
          <div className="space-y-1 px-4.5 py-2.5 pr-3">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="usage-granularity" className="text-sm font-medium text-gray-700">
                Group by
              </label>
              <Select value={granularity} onValueChange={(value) => setGranularity(value as UsageGranularity)}>
                <SelectTrigger id="usage-granularity" className="h-8 w-auto border-0 bg-transparent px-1.5 py-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-gray-700">Date range</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button className="min-w-0 justify-end overflow-hidden border-0 bg-transparent px-1.5 py-1 font-normal">
                    <span className="iconify lucide--calendar-range" />
                    <span className="truncate">
                      {draftDateRange.from && draftDateRange.to
                        ? `${format(draftDateRange.from, 'd MMM yyyy')} – ${format(draftDateRange.to, 'd MMM yyyy')}`
                        : 'Select dates'}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="max-w-none bg-white p-1">
                  <Calendar
                    mode="range"
                    selected={draftDateRange}
                    onSelect={(nextDateRange) => {
                      setDraftDateRange(nextDateRange);
                      if (nextDateRange.from && nextDateRange.to) onDateRangeChange(nextDateRange);
                    }}
                    disabled={{ after: today }}
                    buttonVariant="default"
                    numberOfMonths={2}
                    required
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <hr />

          <div className="space-y-1 px-4.5 py-2.5 pr-3">
            <div className="flex h-8 items-center justify-between gap-3">
              <label htmlFor="show-cumulative" className="cursor-pointer text-sm font-medium text-gray-700">
                Show cumulative
              </label>
              <Switch id="show-cumulative" checked={showCumulative} onCheckedChange={setShowCumulative} />
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
